import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Reply, ExternalLink, AlertCircle, MessageSquare, Mail, Paperclip, Download } from "lucide-react";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import { getAuthTokens } from "@/utils/auth";

const PRIORITY_CONFIG = {
    CRITICAL: { bg: "#FEF2F2", color: "#DC2626", label: "! Critical Priority" },
    HIGH: { bg: "#FFF7ED", color: "#EA580C", label: "↑ High Priority" },
    NORMAL: { bg: "#F0F9FF", color: "#0369A1", label: "Normal" },
} as const;

const TYPE_ICON: Record<string, React.ReactNode> = {
    alert: <AlertCircle className="w-5 h-5" />,
    message: <MessageSquare className="w-5 h-5" />,
    email: <Mail className="w-5 h-5" />,
    file: <Paperclip className="w-5 h-5" />,
};

function formatDate(sentAt?: string): string {
    if (!sentAt) return "";
    try {
        return new Date(sentAt).toLocaleDateString("en-US", {
            weekday: "long", year: "numeric", month: "long", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return sentAt;
    }
}

export default function NotificationDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user: authUser, logout } = useAuth();
    const [acknowledged, setAcknowledged] = useState(false);
    const [notification, setNotification] = useState<any>(null);
    const [userData, setUserData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authUser?.uid || !id) return;
        const fetchData = async () => {
            try {
                const tokens = getAuthTokens();
                const idToken = tokens?.idToken;
                const resp = await fetch(`${API_BASE_URL}/auth/user/${authUser.uid}`, {
                    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
                });
                if (resp.ok) {
                    const json = await resp.json();
                    const ud = json.success ? json.user : json;
                    setUserData(ud);
                    const rawNotifs = ud?.notifications;
                    if (rawNotifs && typeof rawNotifs === "object") {
                        const found = rawNotifs[id];
                        if (found) setNotification({ id, ...found });
                    }
                }
            } catch (e) {
                console.error("Error fetching notification:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [authUser, id]);

    const realIrScore = userData?.irScore?.totalScore ?? null;
    const profileCompletion = (() => {
        if (!userData) return 0;
        let pct = 0;
        if (userData.firstName && userData.lastName) pct += 25;
        if (userData.email) pct += 15;
        if (userData.phone) pct += 10;
        if (userData.goalAssessment?.group) pct += 20;
        if (userData.investorReadinessForm1) pct += 15;
        if (userData.irScore?.totalScore) pct += 15;
        return pct;
    })();

    if (loading) {
        return (
            <div className="flex min-h-screen bg-[#F3F4F6]">
                <Sidebar />
                <main className="flex-1 md:ms-64 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
                        <p className="text-sm text-gray-400">Loading notification...</p>
                    </div>
                </main>
            </div>
        );
    }

    if (!notification) {
        return (
            <div className="flex min-h-screen bg-[#F3F4F6]">
                <Sidebar />
                <main className="flex-1 md:ms-64 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-gray-500 font-medium mb-3">Notification not found</p>
                        <button
                            onClick={() => navigate("/notifications")}
                            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Notifications
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    const priority = (notification.priority as keyof typeof PRIORITY_CONFIG) || "NORMAL";
    const pc = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.NORMAL;
    const senderInitials = (notification.sender || "A").split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();

    return (
        <div className="flex min-h-screen bg-[#F3F4F6]">
            <Sidebar />
            <main className="flex-1 md:ms-64 overflow-y-auto">
                <MobileNav onLogout={logout} />

                <div className="mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8 py-6 md:py-8">

                    {/* Back nav */}
                    <button
                        onClick={() => navigate("/notifications")}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Notification Center
                    </button>

                    <div className="flex flex-col lg:flex-row gap-5 items-start">

                        {/* Main content */}
                        <div className="flex-1 min-w-0">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

                                {/* Top bar */}
                                <div className="flex items-center justify-between px-7 pt-6 pb-0">
                                    <span
                                        className="text-xs font-bold px-3 py-1.5 rounded-full"
                                        style={{ backgroundColor: pc.bg, color: pc.color }}
                                    >
                                        {pc.label}
                                    </span>
                                    <span className="text-xs text-gray-400">{formatDate(notification.sentAt || notification.timestamp)}</span>
                                </div>

                                {/* Title */}
                                <div className="px-7 pt-4 pb-5 border-b border-slate-100">
                                    <h1 className="text-2xl font-black text-gray-900 leading-snug">{notification.title}</h1>
                                    {notification.subtitle && (
                                        <p className="text-sm text-gray-500 mt-2">{notification.subtitle}</p>
                                    )}
                                </div>

                                {/* Sender card */}
                                <div className="mx-7 mt-5 mb-6 rounded-xl flex items-center justify-between px-4 py-3" style={{ backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" }}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black text-white shrink-0" style={{ backgroundColor: "#1D4ED8" }}>
                                            {senderInitials}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">{notification.sender || "Admin"}</p>
                                            <p className="text-xs text-gray-500">{notification.senderRole || "Admin"}</p>
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-black text-blue-600 tracking-wide border border-blue-200 bg-blue-50 px-2.5 py-1 rounded-full">
                                        VERIFIED ADMIN
                                    </span>
                                </div>

                                {/* Type icon + message */}
                                <div className="px-7 pb-6 space-y-4">
                                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-widest">
                                        <span style={{ color: "#6B7280" }}>{TYPE_ICON[notification.type] || TYPE_ICON.message}</span>
                                        <span>{notification.type || "message"}</span>
                                    </div>

                                    {(notification.preview || notification.message) && (() => {
                                        const raw = notification.preview || notification.message || '';
                                        const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                                        return isHtml ? (
                                            <div
                                                className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{ __html: raw }}
                                            />
                                        ) : (
                                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{raw}</p>
                                        );
                                    })()}

                                    {/* Attachments */}
                                    {notification.attachments && Array.isArray(notification.attachments) && notification.attachments.length > 0 && (
                                        <div className="space-y-3 pt-2">
                                            {notification.attachments.map((att: any, i: number) => {
                                                const isImg = att.type?.startsWith('image/');
                                                const isData = typeof att.url === 'string' && att.url.startsWith('data:');
                                                if (isImg) {
                                                    return (
                                                        <div key={i} className="space-y-1.5">
                                                            <img src={att.url} alt={att.name}
                                                                className="w-full rounded-2xl border border-gray-200 object-cover max-h-80 hover:opacity-95 transition" />
                                                            <a
                                                                href={att.url}
                                                                download={att.name}
                                                                rel="noopener noreferrer"
                                                                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 transition"
                                                            >
                                                                <Download className="w-3.5 h-3.5" />
                                                                Download {att.name}
                                                            </a>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <a key={i}
                                                        href={att.url}
                                                        {...(isData ? { download: att.name } : { target: "_blank", rel: "noopener noreferrer" })}
                                                        className="flex items-center gap-3 p-3.5 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 transition group">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                                                            <Paperclip className="w-5 h-5 text-blue-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600">{att.name}</p>
                                                            <p className="text-xs text-gray-400">
                                                                {att.size ? (att.size < 1024 * 1024 ? `${(att.size / 1024).toFixed(1)} KB` : `${(att.size / 1024 / 1024).toFixed(1)} MB`) : 'Download'}
                                                            </p>
                                                        </div>
                                                        {isData
                                                            ? <Download className="w-4 h-4 text-gray-400 group-hover:text-blue-600 shrink-0" />
                                                            : <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 shrink-0" />
                                                        }
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Footer actions */}
                                <div className="flex flex-wrap items-center gap-3 px-7 pb-7 pt-2 border-t border-slate-100">
                                    {notification.ctaLabel && notification.ctaUrl && (
                                        <a
                                            href={notification.ctaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-90"
                                            style={{ backgroundColor: "#1D74F5" }}
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            {notification.ctaLabel}
                                        </a>
                                    )}
                                    <button
                                        onClick={() => navigate(`/message-admin?replyTo=${id}&replyTitle=${encodeURIComponent(notification.title || '')}`)}
                                        className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
                                    >
                                        <Reply className="w-4 h-4" />
                                        Reply to Admin
                                    </button>
                                    <button
                                        onClick={() => setAcknowledged(true)}
                                        disabled={acknowledged}
                                        className="flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold text-white transition active:scale-95 shadow-sm"
                                        style={{ backgroundColor: acknowledged ? "#10B981" : "#1D74F5", cursor: acknowledged ? "default" : "pointer" }}
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                        {acknowledged ? "Acknowledged" : "Acknowledge Receipt"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right sidebar */}
                        <div className="w-full lg:w-64 shrink-0 space-y-4">

                            {/* Sender Profile */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Sender Profile</p>
                                <div className="flex flex-col items-center text-center mb-4">
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black text-white mb-3" style={{ backgroundColor: "#1D4ED8" }}>
                                        {senderInitials}
                                    </div>
                                    <p className="text-sm font-bold text-gray-900">{notification.sender || "Admin"}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">{notification.senderRole || "Admin"} • Conncct Platform</p>
                                </div>
                                <p className="text-[11px] text-gray-500 mt-3 leading-relaxed text-center">
                                    This message was sent from the Conncct Admin panel on your behalf.
                                </p>
                            </div>

                            {/* Compliance Status */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Compliance Status</p>
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-gray-700">Profile Completion</span>
                                        <span className="text-xs font-black text-blue-600">{profileCompletion}%</span>
                                    </div>
                                    <div className="w-full h-2 rounded-full bg-gray-100">
                                        <div
                                            className="h-2 rounded-full transition-all duration-700"
                                            style={{ width: `${profileCompletion}%`, backgroundColor: "#1D74F5" }}
                                        />
                                    </div>
                                </div>
                                <p className="text-[11px] text-gray-500 leading-relaxed mb-3">
                                    {realIrScore !== null
                                        ? <>Your current IR Score is <span className="font-bold text-gray-800">{realIrScore}</span>.</>
                                        : <>Complete your IR assessment to unlock your score.</>
                                    }
                                </p>
                                <button
                                    onClick={() => navigate("/ir-score")}
                                    className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 transition"
                                >
                                    Go to IR Dashboard
                                    <ExternalLink className="w-3 h-3" />
                                </button>
                            </div>

                            {/* Need Help */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Need Help?</p>
                                <p className="text-xs text-gray-600 leading-relaxed mb-4">
                                    Have questions about this notification? Speak with a Conncct expert.
                                </p>
                                <button
                                    onClick={() => navigate(`/message-admin?replyTo=${id}&replyTitle=${encodeURIComponent(notification.title || '')}`)}
                                    className="w-full rounded-xl border-2 border-gray-900 bg-white py-2.5 text-sm font-bold text-gray-900 hover:bg-gray-900 hover:text-white transition"
                                >
                                    Message Admin
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
